import {
  createTimezoneDayjs,
  dayjs,
  formatDayjsToString,
  isValidDayjs,
  parseDateString,
  parseTimeString
} from './dayjs-wrapper';
import { convertDateFormatToDayjs, convertTimeFormatToDayjs } from './formatters';
import type { DateFormat, TimeFormat } from './types';

export const convertDateTimeRangeToIsoWithTimezone = (
  dateTimeRange: { startDate: string; startTime: string; endDate: string; endTime: string },
  courseTimezone: string
): { startIso: string; endIso: string } => {
  const startDateTime = `${dateTimeRange.startDate}T${dateTimeRange.startTime}`;
  const endDateTime = `${dateTimeRange.endDate}T${dateTimeRange.endTime}`;

  const startInCourseTz = createTimezoneDayjs(startDateTime, courseTimezone);
  const endInCourseTz = createTimezoneDayjs(endDateTime, courseTimezone);

  if (!isValidDayjs(startInCourseTz) || !isValidDayjs(endInCourseTz)) {
    return { startIso: '', endIso: '' };
  }

  return {
    startIso: startInCourseTz.utc().toISOString(),
    endIso: endInCourseTz.utc().toISOString()
  };
};

export const convertDateTimeRangeToCourseTimezoneIso = (
  dateTimeRange: { startDate: string; startTime: string; endDate: string; endTime: string },
  courseTimezone: string,
  dateFormat: string
): { startIso: string; endIso: string } => {
  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat as DateFormat);

  const startDateTime = `${dateTimeRange.startDate}T${dateTimeRange.startTime}`;
  const endDateTime = `${dateTimeRange.endDate}T${dateTimeRange.endTime}`;

  const startInCourseTz = createTimezoneDayjs(startDateTime, courseTimezone);
  const endInCourseTz = createTimezoneDayjs(endDateTime, courseTimezone);

  if (!isValidDayjs(startInCourseTz) || !isValidDayjs(endInCourseTz)) {
    return { startIso: '', endIso: '' };
  }

  const isoFormat = `${dayjsDateFormat}THH:mm:ss.SSS[Z]`;

  return {
    startIso: startInCourseTz.format(isoFormat),
    endIso: endInCourseTz.format(isoFormat)
  };
};

export const convertDateTimeRangeToTimezoneAware = (
  dateTimeRange: { startDate: string; startTime: string; endDate: string; endTime: string },
  courseTimezone: string,
  dateFormat: string,
  timeFormat: string
): { startDate: string; endDate: string } => {
  const dayjsDateFormat = convertDateFormatToDayjs(dateFormat as DateFormat);
  const dayjsTimeFormat = convertTimeFormatToDayjs(timeFormat as TimeFormat);

  const startDateTime = `${dateTimeRange.startDate}T${dateTimeRange.startTime}`;
  const endDateTime = `${dateTimeRange.endDate}T${dateTimeRange.endTime}`;

  const startInCourseTz = createTimezoneDayjs(startDateTime, courseTimezone);
  const endInCourseTz = createTimezoneDayjs(endDateTime, courseTimezone);

  if (!isValidDayjs(startInCourseTz) || !isValidDayjs(endInCourseTz)) {
    return { startDate: '', endDate: '' };
  }

  const timezoneAwareFormat = `${dayjsDateFormat} ${dayjsTimeFormat}`;

  return {
    startDate: formatDayjsToString(startInCourseTz, timezoneAwareFormat),
    endDate: formatDayjsToString(endInCourseTz, timezoneAwareFormat)
  };
};

export const convertLocalDateTimeRangeToIso = (dateTimeRange: {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}): { startIso: string; endIso: string } => {
  const startDateTime = `${dateTimeRange.startDate}T${dateTimeRange.startTime}`;
  const endDateTime = `${dateTimeRange.endDate}T${dateTimeRange.endTime}`;

  const startDayjs = dayjs(startDateTime);
  const endDayjs = dayjs(endDateTime);

  if (!isValidDayjs(startDayjs) || !isValidDayjs(endDayjs)) {
    return { startIso: '', endIso: '' };
  }

  return {
    startIso: startDayjs.toISOString(),
    endIso: endDayjs.toISOString()
  };
};

export const convertCourseDatesFormat = <
  T extends { startDate: string; endDate: string; startTime: string; endTime: string }
>(
  courseDates: T[],
  fromDateFormat: DateFormat,
  fromTimeFormat: TimeFormat,
  toDateFormat: DateFormat,
  toTimeFormat: TimeFormat
): T[] => {
  const fromDayjsDateFormat = convertDateFormatToDayjs(fromDateFormat);
  const fromDayjsTimeFormat = convertTimeFormatToDayjs(fromTimeFormat);
  const toDayjsDateFormat = convertDateFormatToDayjs(toDateFormat);
  const toDayjsTimeFormat = convertTimeFormatToDayjs(toTimeFormat);

  return courseDates.map(courseDate => {
    const startDateParsed = parseDateString(courseDate.startDate, fromDayjsDateFormat);
    const endDateParsed = parseDateString(courseDate.endDate, fromDayjsDateFormat);
    const startTimeParsed = parseTimeString(courseDate.startTime, fromDayjsTimeFormat);
    const endTimeParsed = parseTimeString(courseDate.endTime, fromDayjsTimeFormat);

    const newStartDate = startDateParsed
      ? startDateParsed.format(toDayjsDateFormat)
      : courseDate.startDate;
    const newEndDate = endDateParsed ? endDateParsed.format(toDayjsDateFormat) : courseDate.endDate;
    const newStartTime = startTimeParsed
      ? startTimeParsed.format(toDayjsTimeFormat)
      : courseDate.startTime;
    const newEndTime = endTimeParsed ? endTimeParsed.format(toDayjsTimeFormat) : courseDate.endTime;

    return {
      ...courseDate,
      startDate: newStartDate,
      endDate: newEndDate,
      startTime: newStartTime,
      endTime: newEndTime
    };
  });
};
